import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { combineLatest, map, Observable, take } from 'rxjs';

import { Steps } from '@app/components/stepper/stepper.component';
import { PortalRoutes } from '@app/app-routing.module';
import { PortalCurrencyPipe } from '@app/pipes/portal-currency.pipe';
import { handleError } from '@app/operators/handle-error';
import { ErrorService } from '@app/services/error.service';
import { AuthService } from '@app/services/auth/auth.service';
import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { PortalTierCatalogService } from '@app/services/portal-tier-catalog/portal-tier-catalog.service';
import { StateService } from '@app/services/state/state.service';
import {
  checkoutExtrasFromState,
  SessionProceedCoordinator,
} from '@app/services/session-proceed-coordinator.service';
import {
  PortalOrderNode,
  PortalTierCatalogNode,
  PortalTierEnum,
} from '@app/services/api/api.types';
import { PopupsService, PriceSummaryAction } from '@voyagu/portal-design-system';
import { MemoizedValue } from '@app/utils/memoized-value.util';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import {
  getActiveTierAncillary,
  getTierForRetention,
  getTierRetentionBannerBody,
  getTierRetentionVariant,
  TierPlan,
  TierRetentionVariant,
} from '@app/pages/tiers-page/utils';

@Component({
  selector: 'tier-change-page',
  templateUrl: './tier-change-page.component.html',
  styleUrl: './tier-change-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SessionProceedCoordinator],
})
export class TierChangePageComponent implements OnInit {
  Steps = Steps;

  router = inject(Router);
  location = inject(Location);
  stateService = inject(StateService);
  portalOrderService = inject(PortalOrderService);
  portalLeadService = inject(PortalLeadService);
  portalTierCatalogService = inject(PortalTierCatalogService);
  portalCurrencyPipe = inject(PortalCurrencyPipe);
  popupsService = inject(PopupsService);
  authService = inject(AuthService);
  errorService = inject(ErrorService);
  sessionProceed = inject(SessionProceedCoordinator);

  portalOrder$: Observable<PortalOrderNode | null>;
  portalOrder: PortalOrderNode | null;

  tier: TierPlan | null;
  planName = '';
  variant: TierRetentionVariant = 'zeroFareDiff';
  bannerTitle = 'Your booking option remains active';
  bannerBody = '';
  planCardLabel = 'YOUR CURRENT PLAN';
  changeTierType?: PortalTierEnum;

  isNewUser = false;

  @ViewChild('askToSignInTemplate') askToSignInTemplate: TemplateRef<unknown>;

  ngOnInit() {
    Tracking.track(PortalTrackingEvents.tiersPageOpened);

    this.portalOrder$ = combineLatest([
      this.portalOrderService.portalOrder$,
      this.portalLeadService.portalLead$,
      this.portalTierCatalogService.catalog$,
    ]).pipe(
      map(([portalOrder, portalLead, catalog]) => {
        if (portalOrder) {
          this.portalOrder = portalOrder;
          this.isNewUser = !!portalLead?.isNewUser;

          if (!portalOrder.isChange) {
            void this.router.navigate([
              this.stateService.getState().portalLink,
              portalOrder.portalLink,
              PortalRoutes.Tiers,
            ]);

            return portalOrder;
          }

          this.initTierChange(portalOrder, portalLead?.salesAgent?.agencyName ?? '', catalog);
        }

        return portalOrder;
      }),
    );
  }

  initTierChange(
    portalOrder: PortalOrderNode,
    agencyName: string,
    catalog: PortalTierCatalogNode[],
  ) {
    const activeAncillary = getActiveTierAncillary(portalOrder);

    if (!activeAncillary) {
      void this.router.navigate([
        this.stateService.getState().portalLink,
        portalOrder.portalLink,
        PortalRoutes.Gratuity,
      ]);

      return;
    }

    const tierData = getTierForRetention(
      catalog,
      portalOrder,
      this.portalCurrencyPipe.transform,
      agencyName,
    );

    if (!tierData?.value) {
      void this.router.navigate([
        this.stateService.getState().portalLink,
        portalOrder.portalLink,
        PortalRoutes.Gratuity,
      ]);

      return;
    }

    this.tier = tierData;
    this.changeTierType = tierData.value as PortalTierEnum;
    this.planName = `${tierData.label} Plan`;

    this.variant = getTierRetentionVariant(portalOrder, activeAncillary);
    this.planCardLabel =
      this.variant === 'zeroFareDiff' ? 'YOUR CURRENT OPTION' : 'YOUR CURRENT PLAN';

    const fareDiffFormatted = this.portalCurrencyPipe.transform(
      portalOrder.soldAmountPerAdult ?? 0,
    );
    const retentionGapFormatted = this.portalCurrencyPipe.transform(
      activeAncillary.amountPerTraveler ?? 0,
    );

    this.bannerBody = getTierRetentionBannerBody(
      this.variant,
      this.planName,
      fareDiffFormatted,
      retentionGapFormatted,
    );

    if (this.variant !== 'payBoth') {
      this.stateService.patchState({ tier: this.changeTierType });
    }
  }

  get showForfeitOption() {
    return this.variant === 'payBoth';
  }

  get showSingleContinue() {
    return this.variant !== 'payBoth';
  }

  summaryActionsCache = new MemoizedValue<PriceSummaryAction[]>();

  get summaryActions(): PriceSummaryAction[] {
    const { showForfeitOption, showSingleContinue, planName } = this;

    return this.summaryActionsCache.get(
      `${showForfeitOption}|${showSingleContinue}|${planName}`,
      () => {
        const actions: PriceSummaryAction[] = [];

        if (showForfeitOption) {
          actions.push(
            { id: 'retain', label: `Continue with ${planName}`, variant: 'primary' },
            { id: 'forfeit', label: 'Continue without protection', variant: 'secondary' },
          );
        } else if (showSingleContinue) {
          actions.push({ id: 'continue', label: 'Next: Gratuity →', variant: 'primary' });
        }

        actions.push({ id: 'back', label: 'Back', variant: 'secondary' });

        return actions;
      },
    );
  }

  onSummaryAction(action: PriceSummaryAction) {
    if (action.id === 'back') {
      this.back();
      return;
    }

    if (action.id === 'forfeit') {
      this.continueWithoutProtection();
      return;
    }

    if (action.id === 'retain') {
      this.continueWithPlan();
      return;
    }

    this.continueToGratuity();
  }

  back() {
    this.location.back();
  }

  continueWithPlan() {
    this.submit(true);
  }

  continueWithoutProtection() {
    this.submit(false);
  }

  continueToGratuity() {
    this.submit(true);
  }

  submit(retain: boolean) {
    this.stateService.patchState({ tier: retain ? this.changeTierType : undefined });
    this.checkSession();
  }

  checkSession() {
    this.authService
      .hasSession()
      .pipe(
        take(1),
        map((hasSession) => {
          if (hasSession || this.isNewUser) {
            return this.redirect();
          }
          this.showAskToSignInModal();
        }),
        handleError(this.errorService),
      )
      .subscribe();
  }

  showAskToSignInModal() {
    this.popupsService.showModal(this.askToSignInTemplate);
    this.sessionProceed.trackSignInFromCheckoutPromptOpened();
  }

  showSignInModal() {
    const fromState = checkoutExtrasFromState(this.stateService.getState()) ?? {};

    this.sessionProceed.openSignInModalForPortalOption(
      this.stateService.getState().portalLink,
      this.portalOrder?.portalLink ?? '',
      {
        ...fromState,
        tier:
          this.stateService.getState().tier != null
            ? String(this.stateService.getState().tier)
            : fromState.tier,
        checkoutResume: PortalRoutes.TierChange,
        checkoutPath: 'option',
      },
    );
  }

  redirect() {
    const path = [
      `${this.stateService.getState().portalLink}`,
      `${this.portalOrder?.portalLink}`,
      PortalRoutes.Gratuity,
    ];

    void this.router.navigate(path, { queryParamsHandling: 'merge' });
  }

  hideModal() {
    this.popupsService.hideLast();
  }
}
